import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useAddBookmark, useRemoveBookmark, useServerBookmarks, type BookmarkResponse } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getStored, setStored } from "@/lib/constants";

/* ============================================
   Types
   ============================================ */

export interface Bookmark {
  id: string;
  type: "seerah" | "timeline" | "quran" | "clarifier" | "isnad" | "dhikr";
  title: string;
  subtitle?: string;
  route: string;
  addedAt: string;
}

interface BookmarkContextValue {
  bookmarks: Bookmark[];
  isBookmarked: (id: string) => boolean;
  toggle: (bookmark: Omit<Bookmark, "addedAt">) => boolean; // returns new state
  remove: (id: string) => void;
  clear: () => void;
}

/* ============================================
   Context
   ============================================ */

const BookmarkContext = createContext<BookmarkContextValue | null>(null);

export function useBookmarks() {
  const ctx = useContext(BookmarkContext);
  if (!ctx) throw new Error("useBookmarks must be used within BookmarkProvider");
  return ctx;
}

/** Bookmarks are self-describing, so the server copy stores the display fields in `notes`. */
function toServer(bm: Bookmark) {
  return {
    entityType: bm.type,
    entityId: bm.id,
    notes: JSON.stringify({ title: bm.title, subtitle: bm.subtitle, route: bm.route }),
  };
}

function fromServer(row: BookmarkResponse): Bookmark | null {
  try {
    const meta = JSON.parse(row.notes ?? "{}") as Partial<Bookmark>;
    if (!meta.title || !meta.route) return null;
    return {
      id: row.entityId,
      type: row.entityType as Bookmark["type"],
      title: meta.title,
      subtitle: meta.subtitle,
      route: meta.route,
      addedAt: row.createdAt,
    };
  } catch {
    return null;
  }
}

/**
 * Bookmarks live in localStorage so they work without an account. When the
 * reader is signed in they are also saved to their account, and the account's
 * bookmarks are merged in on sign-in (so they follow the reader across devices).
 */
export function BookmarkProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const syncing = auth.enabled && auth.isAuthenticated;
  const server = useServerBookmarks(syncing);
  const addRemote = useAddBookmark();
  const removeRemote = useRemoveBookmark();

  const [bookmarks, setBookmarks] = useState<Bookmark[]>(() =>
    getStored<Bookmark[]>("bookmarks", []),
  );

  const persist = useCallback((next: Bookmark[]) => {
    setBookmarks(next);
    setStored("bookmarks", next);
  }, []);

  // Merge the account's bookmarks in once they load, and upload local-only ones.
  const merged = useRef(false);
  useEffect(() => {
    if (!syncing || !server.data || merged.current) return;
    merged.current = true;
    const remote = server.data.bookmarks.map(fromServer).filter((b): b is Bookmark => b !== null);
    const remoteIds = new Set(remote.map((b) => b.id));
    setBookmarks((local) => {
      const localOnly = local.filter((b) => !remoteIds.has(b.id));
      localOnly.forEach((b) => addRemote.mutate(toServer(b)));
      const next = [...remote, ...localOnly];
      setStored("bookmarks", next);
      return next;
    });
  }, [syncing, server.data, addRemote]);

  useEffect(() => {
    if (!syncing) merged.current = false;
  }, [syncing]);

  const isBookmarked = useCallback(
    (id: string) => bookmarks.some((b) => b.id === id),
    [bookmarks],
  );

  const toggle = useCallback(
    (bm: Omit<Bookmark, "addedAt">): boolean => {
      const exists = bookmarks.find((b) => b.id === bm.id);
      if (exists) {
        persist(bookmarks.filter((b) => b.id !== bm.id));
        if (syncing) removeRemote.mutate({ entityType: exists.type, entityId: exists.id });
        return false;
      }
      const added = { ...bm, addedAt: new Date().toISOString() };
      persist([...bookmarks, added]);
      if (syncing) addRemote.mutate(toServer(added));
      return true;
    },
    [bookmarks, persist, syncing, addRemote, removeRemote],
  );

  const remove = useCallback(
    (id: string) => {
      const existing = bookmarks.find((b) => b.id === id);
      persist(bookmarks.filter((b) => b.id !== id));
      if (syncing && existing) removeRemote.mutate({ entityType: existing.type, entityId: existing.id });
    },
    [bookmarks, persist, syncing, removeRemote],
  );

  const clear = useCallback(() => {
    if (syncing) bookmarks.forEach((b) => removeRemote.mutate({ entityType: b.type, entityId: b.id }));
    persist([]);
  }, [bookmarks, persist, syncing, removeRemote]);

  return (
    <BookmarkContext.Provider value={{ bookmarks, isBookmarked, toggle, remove, clear }}>
      {children}
    </BookmarkContext.Provider>
  );
}
